import { VERKTOY_START_API_URLS } from '@store/constants/apiURLs';

import { type GetVerktoyLaunchOperationResponse, VERKTOY_PROXY_NAME } from '../types/apiTypes';
import { OppstartType, LenkeType, type Lenke } from '../types/minhelseentities';

import { isAuthorized } from '@helsenorge/framework-utils/hn-authorize';
import { get } from '@helsenorge/framework-utils/hn-proxy-service';
import { sanitize } from '@helsenorge/framework-utils/trusted-types';

const LAUNCH_FEILMELDING = 'Kunne ikke åpne verktøyet.';
const LAUNCH_LOADING_TEKST = 'Åpner verktøy...';

export interface LaunchAktivitetParams {
  verktoyId: string;
  oppstartsType: OppstartType;
  lenker: Lenke[];
  lenkeType: LenkeType;
  kanStartesAvInnloggetBruker: boolean;
  uthopp?: boolean;
}

const aapneUrl = (url: string, uthopp?: boolean): void => {
  if (uthopp) {
    window.location.href = url;
  } else {
    const win = window.open(url, '_blank');
    if (win) {
      win.focus();
    }
  }
};

const loggStartVerktoy = (verktoyId: string): void => {
  get<GetVerktoyLaunchOperationResponse>(VERKTOY_PROXY_NAME, `${VERKTOY_START_API_URLS.LOGG_START_VERKTOY}?verktoyId=${verktoyId}`);
};

const submitLaunchForm = (launchScript: string, doc: Document): void => {
  const container = doc.createElement('div');
  container.innerHTML = sanitize(launchScript);
  const form = container.firstChild as HTMLFormElement;
  doc.body.appendChild(form);
  form.submit();
};

const handleLtiResponse = (response: GetVerktoyLaunchOperationResponse, doc: Document): void => {
  if (response.LaunchScript) {
    submitLaunchForm(response.LaunchScript, doc);
  } else if (response.RedirectUrl) {
    doc.defaultView!.location.href = response.RedirectUrl;
  }
};

const startLtiICurrentTab = async (verktoyId: string): Promise<void> => {
  document.body.innerHTML = sanitize(LAUNCH_LOADING_TEKST);
  try {
    const data = await get<GetVerktoyLaunchOperationResponse>(
      VERKTOY_PROXY_NAME,
      `${VERKTOY_START_API_URLS.START_LTI_VERKTOY}?verktoyId=${verktoyId}`
    );
    handleLtiResponse(data as GetVerktoyLaunchOperationResponse, document);
  } catch {
    document.body.innerHTML = sanitize(LAUNCH_FEILMELDING);
  }
};

const startLtiINyttVindu = async (verktoyId: string): Promise<void> => {
  const launchWindow = window.open('', '_blank');
  if (launchWindow) {
    launchWindow.document.body.innerHTML = sanitize(LAUNCH_LOADING_TEKST);
    launchWindow.focus();
  }

  try {
    const data = await get<GetVerktoyLaunchOperationResponse>(
      VERKTOY_PROXY_NAME,
      `${VERKTOY_START_API_URLS.START_LTI_VERKTOY}?verktoyId=${verktoyId}`
    );
    const response = data as GetVerktoyLaunchOperationResponse;
    if (launchWindow) {
      handleLtiResponse(response, launchWindow.document);
      launchWindow.focus();
    }
  } catch {
    if (launchWindow) {
      launchWindow.document.body.innerHTML = sanitize(LAUNCH_FEILMELDING);
      launchWindow.focus();
    }
  }
};

const viseFeilINyttVindu = (): void => {
  const errorWindow = window.open('', '_blank');
  if (errorWindow) {
    errorWindow.document.body.innerHTML = sanitize(LAUNCH_FEILMELDING);
    errorWindow.focus();
  }
};

export const launchAktivitet = (params: LaunchAktivitetParams): void => {
  const { verktoyId, oppstartsType, lenker, lenkeType, kanStartesAvInnloggetBruker, uthopp } = params;
  const lenkeUrl = lenker.find(lenke => lenke.LenkeType === lenkeType)?.Url;

  const erDirekteUrl = oppstartsType === OppstartType.DirekteUrl;
  const erOIDC = oppstartsType === OppstartType.OIDC;
  const erLTI = oppstartsType === OppstartType.LTIv1_1;
  const erIkkeStandardLenke = lenkeType !== LenkeType.Standard;
  const brukerKanIkkeStarte = !isAuthorized() || !kanStartesAvInnloggetBruker;

  const harDirektelenke = (erDirekteUrl || erIkkeStandardLenke) && lenkeUrl;
  const harAutorisertDirektelenke = isAuthorized() && (erDirekteUrl || erOIDC || erIkkeStandardLenke) && lenkeUrl;

  if (brukerKanIkkeStarte && harDirektelenke) {
    // Uautorisert bruker, men verktøyet har en direktelenke som kan åpnes uten innlogging
    aapneUrl(lenkeUrl, uthopp);
  } else if (harAutorisertDirektelenke) {
    // Autorisert bruker med direktelenke — logg og åpne
    loggStartVerktoy(verktoyId);
    aapneUrl(lenkeUrl, uthopp);
  } else if (erLTI) {
    // LTI-oppstart krever server-side launch via form POST
    if (uthopp) {
      startLtiICurrentTab(verktoyId);
    } else {
      startLtiINyttVindu(verktoyId);
    }
  } else if (!uthopp) {
    viseFeilINyttVindu();
  }
};
